import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Paper,
  IconButton,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  StepButton,
  Tabs,
  Tab,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Close,
  Upload,
  AttachFile,
  Delete,
  AutoFixHigh,
  Lightbulb,
  CheckCircle,
  Description,
  Person,
  Email,
  Phone,
  Work,
  School,
  Star,
  Send,
  Download,
  Refresh,
  SmartToy,
  Fullscreen,
  FullscreenExit,
  FolderOpen,
  InsertDriveFile,
  PictureAsPdf,
  TableChart,
  Slideshow,
  TextSnippet,
  Image,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { UserService } from '../../services/userService';
import { ApplicationService } from '../../services/applicationService';
import AuthModal from '../Auth/AuthModal';
import toast from 'react-hot-toast';

// Define DocumentInfo interface locally to avoid import issues
interface DocumentInfo {
  url: string;
  name: string;
  type: 'cv' | 'cover_letter' | 'portfolio' | 'certificate' | 'other';
  description?: string;
  fileSize?: number;
  mimeType?: string;
}

interface JobApplicationModalProps {
  open: boolean;
  onClose: () => void;
  jobTitle: string;
  companyName: string;
  productId: string;
  sellerId: string;
  onApply: (applicationData: ApplicationData) => void;
}

interface UserDocument {
  id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  document_type: string;
  description?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  url?: string;
}

interface SelectedDocument {
  document: UserDocument;
  purpose: string;
  isRequired: boolean;
}

interface ApplicationData {
  coverLetter: string;
  resume: File | null;
  additionalDocuments: File[];
  selectedDocuments: SelectedDocument[];
  personalInfo: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    linkedin?: string;
    portfolio?: string;
  };
  experience: string;
  education: string;
  skills: string[];
  motivation: string;
}

const JobApplicationModal: React.FC<JobApplicationModalProps> = ({
  open,
  onClose,
  jobTitle,
  companyName,
  productId,
  sellerId,
  onApply,
}) => {
  const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
  const theme = useTheme();

  const formatErrorMessage = (e: unknown): string => {
    if (!e) return 'Unknown error';
    if (e instanceof Error) return e.message;
    if (typeof e === 'string') return e;
    if (typeof e === 'object') {
      const anyErr: any = e as any;
      return (
        anyErr?.message ||
        anyErr?.error ||
        anyErr?.error_description ||
        anyErr?.details ||
        anyErr?.hint ||
        (() => {
          try { return JSON.stringify(anyErr); } catch { return String(anyErr); }
        })()
      );
    }
    return String(e);
  };

  const [activeStep, setActiveStep] = useState(0);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [showAiGuide, setShowAiGuide] = useState(false);
  const [applicationMode, setApplicationMode] = useState<'manual' | 'ai' | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [aiGeneratingAnalysis, setAiGeneratingAnalysis] = useState(false);
  const [aiInsights, setAiInsights] = useState<string[]>([]);
  const [aiPersonality, setAiPersonality] = useState<string>('');
  const [aiTone, setAiTone] = useState<string>('');
  const [aiGeneratedContent, setAiGeneratedContent] = useState<any>({});
  const [aiConversation, setAiConversation] = useState<any[]>([]);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [maxReachedStep, setMaxReachedStep] = useState(0);

  const brandPrimary = theme.palette.primary.main;
  const brandSecondary = theme.palette.secondary.main;
  const brandGradient = `linear-gradient(135deg, ${brandPrimary} 0%, ${brandSecondary} 100%)`;
  const logoUrl = settings?.logoPath || '';
  
  const [applicationData, setApplicationData] = useState<ApplicationData>({
    coverLetter: '',
    resume: null,
    additionalDocuments: [],
    selectedDocuments: [],
    personalInfo: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      linkedin: '',
      portfolio: '',
    },
    experience: '',
    education: '',
    skills: [],
    motivation: '',
  });

  // Document-related state
  const [userDocuments, setUserDocuments] = useState<UserDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);
  const [editingDocumentPurpose, setEditingDocumentPurpose] = useState<string | null>(null);
  const [showCvSelector, setShowCvSelector] = useState(false);
  const [selectedCvFromLibrary, setSelectedCvFromLibrary] = useState<UserDocument | null>(null);
  const [showAdditionalDocsSelector, setShowAdditionalDocsSelector] = useState(false);
  const [selectedAdditionalDocsFromLibrary, setSelectedAdditionalDocsFromLibrary] = useState<UserDocument[]>([]);
  const [loadingUserProfile, setLoadingUserProfile] = useState(false);

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<'login' | 'register'>('login');
  const [hasSession, setHasSession] = useState(false);

  // Document purpose options
  const DOCUMENT_PURPOSES = [
    { value: 'cv', label: 'CV/Resume', icon: '👤', required: true },
    { value: 'portfolio', label: 'Portfolio', icon: '🎨', required: false },
    { value: 'cover_letter', label: 'Cover Letter', icon: '📝', required: false },
    { value: 'certificate', label: 'Certificate', icon: '🏆', required: false },
    { value: 'reference', label: 'Reference Letter', icon: '📋', required: false },
    { value: 'transcript', label: 'Academic Transcript', icon: '🎓', required: false },
    { value: 'portfolio_work', label: 'Work Samples', icon: '💼', required: false },
    { value: 'other', label: 'Other', icon: '📄', required: false },
  ];

  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalFilesRef = useRef<HTMLInputElement>(null);
  const { user, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (!open) {
      setHasSession(false);
      return;
    }
    supabase.auth
      .getSession()
      .then(({ data }) => {
        setHasSession(Boolean(data?.session?.user));
      })
      .catch(() => {
        setHasSession(false);
      });
  }, [open, authModalOpen]);

  useEffect(() => {
    if (!hasSession) {
      setShowCvSelector(false);
      setShowAdditionalDocsSelector(false);
      setShowDocumentSelector(false);
    }
  }, [hasSession]);

  const ensureAuthenticated = async (): Promise<{ userId: string } | null> => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;

      const sessionUser = data?.session?.user;
      if (!sessionUser) {
        setAuthModalTab('login');
        setAuthModalOpen(true);
        return null;
      }

      return { userId: sessionUser.id };
    } catch (e) {
      console.error('Error checking authentication:', e);
      setAuthModalTab('login');
      setAuthModalOpen(true);
      return null;
    }
  };

  const steps = [
    'Choose Application Mode',
    'AI Analysis & Setup',
    'Personal Information',
    'Experience & Education',
    'AI Cover Letter Generation',
    'Documents & Review',
    'Final Review & Submit'
  ];

  const handleNext = () => {
    if (activeStep === 0 && !applicationMode) {
      return; // Don't proceed without selecting a mode
    }
    setMaxReachedStep((prev) => Math.max(prev, activeStep + 1));
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setApplicationMode(null);
    setMaxReachedStep(0);
    
    // Reset all data but keep user's personal info
    const resetData = {
      coverLetter: '',
      resume: null,
      additionalDocuments: [],
      selectedDocuments: [],
      personalInfo: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        linkedin: '',
        portfolio: '',
      },
      experience: '',
      education: '',
      skills: [],
      motivation: '',
    };
    
    setApplicationData(resetData);
    setSelectedCvFromLibrary(null);
    setShowCvSelector(false);
    setSelectedAdditionalDocsFromLibrary([]);
    setShowAdditionalDocsSelector(false);
    
    // Reload user profile data after reset
    if (user) {
      loadUserProfileData();
    }
  };

  // Document loading functions
  const loadUserDocuments = async () => {
    if (!user) return;
    
    setLoadingDocuments(true);
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const dbDocs = Array.isArray(data) ? data : [];

      // Also list any files directly from the user-documents bucket under the user's folder
      const { data: storageFiles, error: listError } = await supabase.storage
        .from('user-documents')
        .list(user.id, { limit: 1000, offset: 0 });

      if (listError) {
        console.warn('Could not list user-documents storage files:', listError);
      }

      const existingPaths = new Set((dbDocs || []).map((d: any) => d.file_path));
      const synthesizedFromStorage = (storageFiles || [])
        .filter((f: any) => f?.name && f.name !== '.emptyFolderPlaceholder')
        .filter((f: any) => !existingPaths.has(`${user.id}/${f.name}`))
        .map((f: any) => {
          const name: string = f.name;
          const ext = (name.split('.').pop() || '').toLowerCase();
          const mime = inferMimeFromExtension(ext);
          return ({
          // Synthesize a UserDocument-like shape for selection
          id: `${user.id}/${f.name}`,
          user_id: user.id,
          file_name: f.name,
          file_path: `${user.id}/${f.name}`,
          file_size: (f as any).metadata?.size ?? (f.size ?? 0),
          file_type: mime,
          document_type: 'other',
          description: null,
          is_public: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          });
        });

      const combined = [...dbDocs, ...synthesizedFromStorage];

      // Get signed URLs for each document
      const documentsWithUrls = await Promise.all(
        combined.map(async (doc: any) => {
          const { data: urlData, error: signedErr } = await supabase.storage
            .from('user-documents')
            .createSignedUrl(doc.file_path, 3600);
          if (signedErr) {
            // Skip files that no longer exist
            return null as any;
          }
          return {
            ...doc,
            url: urlData?.signedUrl || ''
          } as UserDocument;
        })
      );

      setUserDocuments(documentsWithUrls.filter(Boolean));
    } catch (err) {
      console.error('Error loading documents:', err);
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleSelectDocument = (document: UserDocument, purpose: string = 'other') => {
    const isSelected = applicationData.selectedDocuments.some(doc => doc.document.id === document.id);
    
    if (isSelected) {
      // Remove document
      setApplicationData(prev => ({
        ...prev,
        selectedDocuments: prev.selectedDocuments.filter(doc => doc.document.id !== document.id)
      }));
    } else {
      // Add document with purpose
      const purposeConfig = DOCUMENT_PURPOSES.find(p => p.value === purpose) || DOCUMENT_PURPOSES[DOCUMENT_PURPOSES.length - 1];
      const newSelectedDocument: SelectedDocument = {
        document,
        purpose,
        isRequired: purposeConfig.required
      };
      
      setApplicationData(prev => ({
        ...prev,
        selectedDocuments: [...prev.selectedDocuments, newSelectedDocument]
      }));
    }
  };

  const handleRemoveSelectedDocument = (documentId: string) => {
    setApplicationData(prev => ({
      ...prev,
      selectedDocuments: prev.selectedDocuments.filter(doc => doc.document.id !== documentId)
    }));
  };

  const handleUpdateDocumentPurpose = (documentId: string, newPurpose: string) => {
    setApplicationData(prev => ({
      ...prev,
      selectedDocuments: prev.selectedDocuments.map(doc => 
        doc.document.id === documentId 
          ? { ...doc, purpose: newPurpose }
          : doc
      )
    }));
    setEditingDocumentPurpose(null);
  };

  const getPurposeConfig = (purpose: string) => {
    return DOCUMENT_PURPOSES.find(p => p.value === purpose) || DOCUMENT_PURPOSES[DOCUMENT_PURPOSES.length - 1];
  };

  const handleSelectCvFromLibrary = (document: UserDocument) => {
    setSelectedCvFromLibrary(document);
    setShowCvSelector(false);
    // Clear uploaded resume when selecting from library
    setApplicationData(prev => ({
      ...prev,
      resume: null
    }));
  };

  const handleUploadNewCv = () => {
    setSelectedCvFromLibrary(null);
    setShowCvSelector(false);
    // Clear library selection when uploading new
    fileInputRef.current?.click();
  };

  const handleRemoveCv = () => {
    setSelectedCvFromLibrary(null);
    setApplicationData(prev => ({
      ...prev,
      resume: null
    }));
  };

  const handleSelectAdditionalDocFromLibrary = (document: UserDocument) => {
    const isSelected = selectedAdditionalDocsFromLibrary.some(doc => doc.id === document.id);
    
    if (isSelected) {
      // Remove document
      setSelectedAdditionalDocsFromLibrary(prev => 
        prev.filter(doc => doc.id !== document.id)
      );
    } else {
      // Add document
      setSelectedAdditionalDocsFromLibrary(prev => [...prev, document]);
    }
  };

  const handleUploadNewAdditionalDocs = () => {
    setSelectedAdditionalDocsFromLibrary([]);
    setShowAdditionalDocsSelector(false);
    // Clear library selection when uploading new
    additionalFilesRef.current?.click();
  };

  const handleRemoveAdditionalDoc = (documentId: string) => {
    setSelectedAdditionalDocsFromLibrary(prev => 
      prev.filter(doc => doc.id !== documentId)
    );
  };

  const handleRemoveAllAdditionalDocs = () => {
    setSelectedAdditionalDocsFromLibrary([]);
    setApplicationData(prev => ({
      ...prev,
      additionalDocuments: []
    }));
  };

  const getFileIcon = (type: string) => {
    const t = (type || '').toLowerCase();
    if (t.includes('pdf') || t === 'application/pdf') return <PictureAsPdf color="error" />;
    if (t.includes('image')) return <Image color="primary" />;
    if (t.includes('excel') || t.includes('spreadsheet') || t.includes('sheet')) return <TableChart color="success" />;
    if (t.includes('powerpoint') || t.includes('presentation') || t.includes('ppt')) return <Slideshow color="warning" />;
    if (t.includes('text') || t.includes('plain')) return <TextSnippet color="info" />;
    return <InsertDriveFile color="action" />;
  };

  const inferMimeFromExtension = (ext: string): string => {
    switch (ext) {
      case 'pdf': return 'application/pdf';
      case 'doc': return 'application/msword';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'ppt': return 'application/vnd.ms-powerpoint';
      case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'xls': return 'application/vnd.ms-excel';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'csv': return 'text/csv';
      case 'txt': return 'text/plain';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'png': return 'image/png';
      case 'gif': return 'image/gif';
      default: return 'application/octet-stream';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Load documents when modal opens
  useEffect(() => {
    if (open && user) {
      loadUserDocuments();
    }
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    fetch('/website-settings.json')
      .then(r => r.json())
      .then(setSettings)
      .catch(() => setSettings(null));
  }, [open]);

  // Load user profile data when modal opens
  useEffect(() => {
    if (open && user) {
      loadUserProfileData();
    }
  }, [open, user]);

  const loadUserProfileData = async () => {
    if (!user) {
      console.log('❌ No user found, cannot load profile data');
      return;
    }
    
    console.log('🔄 Loading user profile data for user ID:', user.id);
    setLoadingUserProfile(true);
    
    try {
      const userProfile = await UserService.getUserProfile(user.id);
      console.log('📊 User profile data received:', userProfile);
      
      if (userProfile) {
        console.log('🔍 Full user profile data:', userProfile);
        console.log('🔍 work_experience:', userProfile.work_experience);
        console.log('🔍 education:', userProfile.education);
        
        const personalInfo = {
          first_name: userProfile.first_name || '',
          last_name: userProfile.last_name || '',
          email: userProfile.email || '',
          phone: userProfile.phone || '',
          linkedin: userProfile.social_media_links?.linkedin || userProfile.social_media_links?.linkedin_url || '',
          portfolio: userProfile.website || ''
        };
        
        console.log('✅ Setting personal info:', personalInfo);
        console.log('✅ Setting experience:', userProfile.work_experience || '');
        console.log('✅ Setting education:', userProfile.education || '');
        console.log('🔍 Full userProfile object keys:', Object.keys(userProfile));
        
        setApplicationData(prev => ({
          ...prev,
          personalInfo,
          experience: userProfile.work_experience || '',
          education: userProfile.education || ''
        }));
      } else {
        console.log('❌ No user profile data found');
        toast.error('Could not load your profile data. Please fill out the form manually.');
      }
    } catch (error) {
      console.error('❌ Error loading user profile:', error);
      
      // Fallback: Try direct database query
      try {
        console.log('🔄 Trying direct database query for work_experience and education...');
        const { data: directData, error: directError } = await supabase
          .from('users')
          .select('work_experience, education')
          .eq('id', user.id)
          .single();
          
        if (directError) {
          console.error('❌ Direct query also failed:', directError);
        } else {
          console.log('✅ Direct query successful:', directData);
          setApplicationData(prev => ({
            ...prev,
            experience: directData.work_experience || '',
            education: directData.education || ''
          }));
          toast.success('Profile data loaded successfully!');
          return;
        }
      } catch (fallbackError) {
        console.error('❌ Fallback query failed:', fallbackError);
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Error loading profile: ${errorMessage}`);
    } finally {
      setLoadingUserProfile(false);
    }
  };

  const generatePersonalInfo = () => {
    // Generate realistic sample personal information
    const sampleData = {
      first_name: 'Alex',
      last_name: 'Johnson',
      email: 'alex.johnson@email.com',
      phone: '+1 (555) 123-4567',
      linkedin: 'https://linkedin.com/in/alexjohnson',
      portfolio: 'https://alexjohnson.dev',
    };

    setApplicationData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        ...sampleData
      }
    }));
  };

  const generateProfessionalBackground = () => {
    // Generate realistic sample professional background
    const sampleExperience = `Senior Software Engineer at TechCorp (2020-2024)
• Led development of microservices architecture serving 100K+ users
• Implemented CI/CD pipelines reducing deployment time by 60%
• Mentored 3 junior developers and conducted code reviews
• Collaborated with cross-functional teams to deliver 15+ features

Software Developer at StartupXYZ (2018-2020)
• Built responsive web applications using React and Node.js
• Optimized database queries improving performance by 45%
• Participated in agile development processes and sprint planning`;

    const sampleEducation = `Bachelor of Science in Computer Science
University of Technology (2014-2018)
• GPA: 3.8/4.0
• Relevant Coursework: Data Structures, Algorithms, Software Engineering
• Senior Project: Developed a real-time chat application

Certifications:
• AWS Certified Solutions Architect (2022)
• Google Cloud Professional Developer (2021)
• Certified Scrum Master (2020)`;

    const sampleSkills = [
      'React', 'TypeScript', 'Node.js', 'Python', 'AWS', 
      'Docker', 'Kubernetes', 'PostgreSQL', 'MongoDB', 'Git'
    ];

    setApplicationData(prev => ({
      ...prev,
      experience: sampleExperience,
      education: sampleEducation,
      skills: sampleSkills
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, type: 'resume' | 'additional') => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const incoming = Array.from(files);
      const valid = incoming.filter((f) => f.size <= MAX_UPLOAD_BYTES);
      const rejected = incoming.filter((f) => f.size > MAX_UPLOAD_BYTES);
      if (rejected.length > 0) {
        const names = rejected.map((f) => f.name).join(', ');
        toast.error(`File too large (max 5MB): ${names}`);
      }

      if (type === 'resume') {
        setApplicationData(prev => ({ ...prev, resume: valid[0] || null }));
      } else {
        setApplicationData(prev => ({
          ...prev,
          additionalDocuments: [...prev.additionalDocuments, ...valid]
        }));
      }
    }

    // allow selecting the same file again after rejection
    event.target.value = '';
  };

  const handleStepClick = (targetStep: number) => {
    if (targetStep < 0 || targetStep >= steps.length) return;
    if (targetStep > 0 && !applicationMode) {
      toast.error('Please choose an application mode first');
      setActiveStep(0);
      return;
    }
    setMaxReachedStep((prev) => Math.max(prev, targetStep));
    setActiveStep(targetStep);
  };

  const removeFile = (index: number, type: 'resume' | 'additional') => {
    if (type === 'resume') {
      setApplicationData(prev => ({ ...prev, resume: null }));
    } else {
      setApplicationData(prev => ({
        ...prev,
        additionalDocuments: prev.additionalDocuments.filter((_, i) => i !== index)
      }));
    }
  };

  const addSkill = (skill: string) => {
    if (skill.trim() && !applicationData.skills.includes(skill.trim())) {
      setApplicationData(prev => ({
        ...prev,
        skills: [...prev.skills, skill.trim()]
      }));
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setApplicationData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const generateAICoverLetter = async () => {
    setAiGenerating(true);
    setShowAiGuide(true);
    
    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const suggestions = [
      "Highlight your relevant experience with React and TypeScript",
      "Mention specific projects that demonstrate your problem-solving skills",
      "Show enthusiasm for the company's mission and values",
      "Include quantifiable achievements from your previous roles",
      "Address the job requirements directly in your cover letter"
    ];
    
    setAiSuggestions(suggestions);
    setAiGenerating(false);
  };

  const applyAISuggestion = (suggestion: string) => {
    setApplicationData(prev => ({
      ...prev,
      coverLetter: prev.coverLetter + '\n\n' + suggestion
    }));
  };

  const storeApplicationInStorageFolder = async (options: {
    applicationId: string;
    userId: string;
    documentInfos: DocumentInfo[];
  }): Promise<{ resumePath: string | null; additionalPaths: string[] }> => {
    const target = settings?.uploadTarget || 'supabase';
    const baseFolder = `${options.userId}/${options.applicationId}`;

    const serializeForJson = (data: ApplicationData) => {
      const { resume, additionalDocuments, ...rest } = data;
      return {
        ...rest,
        resume: resume
          ? {
              name: resume.name,
              size: resume.size,
              type: resume.type,
              lastModified: resume.lastModified,
            }
          : null,
        additionalDocuments: (additionalDocuments || []).map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
          lastModified: f.lastModified,
        })),
      };
    };

    if (target === 'supabase') {
      const uploaded = { resumePath: null as string | null, additionalPaths: [] as string[] };
      const payload = {
        applicationId: options.applicationId,
        userId: options.userId,
        productId,
        sellerId,
        jobTitle,
        companyName,
        createdAt: new Date().toISOString(),
        application: serializeForJson(applicationData),
        documents: options.documentInfos,
      };

      const jsonBlob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const jsonFile = new File([jsonBlob], `${options.applicationId}.json`, { type: 'application/json' });

      const uploadBucket = supabase.storage.from('applications');
      const ts = Date.now();

      // Snapshot JSON
      const { error: jsonErr } = await uploadBucket.upload(
        `${baseFolder}/${options.applicationId}.json`,
        jsonFile,
        { upsert: false, contentType: 'application/json' }
      );
      if (jsonErr) throw jsonErr;

      // Newly provided files (local uploads)
      if (applicationData.resume) {
        const safeName = applicationData.resume.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const resumePath = `${baseFolder}/resume/${ts}-resume-${safeName}`;
        const { error: resumeErr } = await uploadBucket.upload(
          resumePath,
          applicationData.resume,
          { upsert: false, contentType: applicationData.resume.type || undefined }
        );
        if (resumeErr) throw resumeErr;
        uploaded.resumePath = resumePath;
      }

      for (const f of applicationData.additionalDocuments || []) {
        const safeName = f.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const addPath = `${baseFolder}/additional-files/${ts}-${safeName}`;
        const { error: addErr } = await uploadBucket.upload(
          addPath,
          f,
          { upsert: false, contentType: f.type || undefined }
        );
        if (addErr) throw addErr;
        uploaded.additionalPaths.push(addPath);
      }

      return uploaded;
    }

    if (target !== 'backblaze') return { resumePath: null, additionalPaths: [] };

    const uploadApiUrl = settings?.backblaze?.uploadApiUrl || '/api/b2-upload.php';
    const backblazeBaseFolder = `applications/${options.applicationId}`;

    const jsonPayload = {
      applicationId: options.applicationId,
      userId: options.userId,
      productId,
      sellerId,
      jobTitle,
      companyName,
      createdAt: new Date().toISOString(),
      application: serializeForJson(applicationData),
      documents: options.documentInfos,
    };

    const jsonBlob = new Blob([JSON.stringify(jsonPayload, null, 2)], { type: 'application/json' });
    const jsonFile = new File([jsonBlob], `${options.applicationId}.json`, { type: 'application/json' });

    const { data: { session } } = await supabase.auth.getSession();
    const authHeaders = session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined;

    const uploadOne = async (file: File, objectKey: string) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', backblazeBaseFolder);
      formData.append('objectKey', objectKey);
      const resp = await fetch(uploadApiUrl, { method: 'POST', body: formData, headers: authHeaders });
      if (!resp.ok) throw new Error(`Storage upload failed (${resp.status})`);
      return resp.json().catch(() => ({}));
    };

    // Upload JSON snapshot
    await uploadOne(jsonFile, `${backblazeBaseFolder}/${options.applicationId}.json`);

    // Upload newly provided files (local uploads)
    const ts = Date.now();
    if (applicationData.resume) {
      const safeName = applicationData.resume.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      await uploadOne(applicationData.resume, `${backblazeBaseFolder}/${ts}-resume-${safeName}`);
    }
    for (const f of applicationData.additionalDocuments || []) {
      const safeName = f.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      await uploadOne(f, `${backblazeBaseFolder}/${ts}-${safeName}`);
    }

    return { resumePath: null, additionalPaths: [] };
  };

  const storeApplicationInLocalStorageFolder = async (options: {
    applicationId: string;
    userId: string;
    documentInfos: DocumentInfo[];
  }) => {
    const serializeForJson = (data: ApplicationData) => {
      const { resume, additionalDocuments, ...rest } = data;
      return {
        ...rest,
        resume: resume
          ? {
              name: resume.name,
              size: resume.size,
              type: resume.type,
              lastModified: resume.lastModified,
            }
          : null,
        additionalDocuments: (additionalDocuments || []).map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
          lastModified: f.lastModified,
        })),
      };
    };

    const payload = {
      applicationId: options.applicationId,
      userId: options.userId,
      productId,
      sellerId,
      jobTitle,
      companyName,
      createdAt: new Date().toISOString(),
      application: serializeForJson(applicationData),
      documents: options.documentInfos,
    };

    const formData = new FormData();
    formData.append('applicationId', options.applicationId);
    formData.append('payload', JSON.stringify(payload));

    if (applicationData.resume) {
      formData.append('resume', applicationData.resume);
    }
    for (const f of applicationData.additionalDocuments || []) {
      formData.append('additionalFiles[]', f);
    }

    const { data: { session } } = await supabase.auth.getSession();
    const authHeaders = session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined;
    const resp = await fetch('/api/store-application.php', { method: 'POST', body: formData, headers: authHeaders });

    const contentType = resp.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const body: any = isJson ? await resp.json().catch(() => null) : await resp.text().catch(() => null);

    if (!resp.ok) {
      let msg = '';
      if (typeof body === 'string') {
        msg = body;
      } else if (body && typeof body === 'object') {
        const firstDetailError = Array.isArray(body?.detail?.errors) ? body.detail.errors[0] : null;
        const detailMsg = firstDetailError?.message || firstDetailError?.error || null;
        msg = detailMsg || body?.error || `Local storage save failed (${resp.status})`;
      } else {
        msg = `Local storage save failed (${resp.status})`;
      }
      throw new Error(msg);
    }

    if (body && typeof body === 'object') {
      if (body.ok === false) {
        throw new Error(body.error || 'Local storage save failed');
      }
      if (Array.isArray(body.errors) && body.errors.length > 0) {
        const first = body.errors[0];
        const detail = first?.message || first?.error || JSON.stringify(first);
        throw new Error(`Local file save failed: ${detail}`);
      }
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent double submission

    const auth = await ensureAuthenticated();
    if (!auth) return;
    
    setIsSubmitting(true);
    
    try {
      // Basic validation
      if (!applicationData.personalInfo.first_name.trim()) {
        toast.error('Please enter your first name');
        setIsSubmitting(false);
        return;
      }
      
      if (!applicationData.personalInfo.last_name.trim()) {
        toast.error('Please enter your last name');
        setIsSubmitting(false);
        return;
      }
      
      if (!applicationData.personalInfo.email.trim()) {
        toast.error('Please enter your email address');
        setIsSubmitting(false);
        return;
      }
      
      if (!applicationData.coverLetter.trim()) {
        toast.error('Please write a cover letter');
        setIsSubmitting(false);
        return;
      }
      
      if (applicationData.coverLetter.trim().length < 10) {
        toast.error('Cover letter must be at least 10 characters long');
        setIsSubmitting(false);
        return;
      }
      
      // Get the current user (session-based check already done)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setAuthModalTab('login');
        setAuthModalOpen(true);
        setIsSubmitting(false);
        return;
      }

      // Collect document info from selected documents
      const documentInfos: DocumentInfo[] = [];
      
      // Add resume document if uploaded
      if (applicationData.resume) {
        // Upload resume to user-documents bucket
        const resumeUploadResult = await uploadResumeToStorage(applicationData.resume, user.id);
        if (resumeUploadResult.success && resumeUploadResult.url) {
          documentInfos.push({
            url: resumeUploadResult.url,
            name: applicationData.resume.name,
            type: 'cv',
            description: 'Resume/CV',
            fileSize: applicationData.resume.size,
            mimeType: applicationData.resume.type
          });
        }
      }
      
      // If CV was chosen from the library, include it
      if (selectedCvFromLibrary) {
        let url = '';
        if (selectedCvFromLibrary.url && selectedCvFromLibrary.url.includes('/user-documents/')) {
          url = selectedCvFromLibrary.url;
        } else if ((selectedCvFromLibrary as any).file_path) {
          const signedUrl = await getSignedUrlForExistingUserDocument((selectedCvFromLibrary as any).file_path);
          if (signedUrl) {
            url = signedUrl;
          }
        }
        
        if (url) {
          documentInfos.push({
            url: url,
            name: selectedCvFromLibrary.file_name || 'CV from library',
            type: 'cv',
            description: 'CV from document library',
            fileSize: selectedCvFromLibrary.file_size,
            mimeType: selectedCvFromLibrary.file_type
          });
        }
      }

      // Add selected documents from library (ensure URL exists; generate signed URL if missing)
      for (const selectedDoc of applicationData.selectedDocuments) {
        const doc = selectedDoc.document;
        let url = '';
        
        if (doc.url && doc.url.includes('/user-documents/')) {
          url = doc.url;
        } else if (doc.file_path) {
          const signedUrl = await getSignedUrlForExistingUserDocument(doc.file_path);
          if (signedUrl) {
            url = signedUrl;
          }
        }
        
        if (url) {
          // Determine document type based on purpose or file name
          let docType: 'cv' | 'cover_letter' | 'portfolio' | 'certificate' | 'other' = 'other';
          const fileName = doc.file_name?.toLowerCase() || '';
          const purpose = selectedDoc.purpose?.toLowerCase() || '';
          
          if (purpose.includes('cv') || purpose.includes('resume') || fileName.includes('cv') || fileName.includes('resume')) {
            docType = 'cv';
          } else if (purpose.includes('cover') || purpose.includes('letter') || fileName.includes('cover')) {
            docType = 'cover_letter';
          } else if (purpose.includes('portfolio') || fileName.includes('portfolio')) {
            docType = 'portfolio';
          } else if (purpose.includes('certificate') || fileName.includes('certificate')) {
            docType = 'certificate';
          }
          
          documentInfos.push({
            url: url,
            name: doc.file_name || 'Document',
            type: docType,
            description: selectedDoc.purpose || 'Supporting document',
            fileSize: doc.file_size,
            mimeType: doc.file_type
          });
        }
      }

      // Upload any additional new documents and collect their info
      if (applicationData.additionalDocuments && applicationData.additionalDocuments.length > 0) {
        for (const file of applicationData.additionalDocuments) {
          const uploadResult = await uploadResumeToStorage(file as File, user.id);
          if (uploadResult.success && uploadResult.url) {
            documentInfos.push({
              url: uploadResult.url,
              name: file.name,
              type: 'other',
              description: 'Additional document',
              fileSize: file.size,
              mimeType: file.type
            });
          }
        }
      }

      // Create the application record (letter + documents) using ApplicationService
      // Fetch product to get company_id
      const { data: productRow } = await supabase
        .from('products')
        .select('id, company_id')
        .eq('id', productId)
        .single();

      const result = await ApplicationService.createApplication({
        title: `${jobTitle} at ${companyName}`,
        letter: applicationData.coverLetter,
        documents: documentInfos,
        productId: productRow?.id || productId,
        companyId: productRow?.company_id || null,
      });

      try {
        const uploadedPaths = await storeApplicationInStorageFolder({
          applicationId: result.id,
          userId: user.id,
          documentInfos,
        });

        // Persist storage object paths on the application row
        if (uploadedPaths.resumePath || (uploadedPaths.additionalPaths && uploadedPaths.additionalPaths.length > 0)) {
          const { error: updErr } = await supabase
            .from('applications')
            .update({
              resume_url: uploadedPaths.resumePath,
              additional_documents_urls: uploadedPaths.additionalPaths,
            })
            .eq('id', result.id);
          if (updErr) throw updErr;
        }
      } catch (e) {
        console.error('Error storing application snapshot in storage:', e);
        const msg = formatErrorMessage(e);
        toast.error(`Application submitted, but saving to storage failed: ${msg}`);
      }

      try {
        await storeApplicationInLocalStorageFolder({
          applicationId: result.id,
          userId: user.id,
          documentInfos,
        });
      } catch (e) {
        console.error('Error storing application snapshot in local storage:', e);
        const msg = formatErrorMessage(e);
        toast.error(`Application submitted, but saving locally failed: ${msg}`);
      }
      
      console.log('Job application submitted successfully:', result);
      toast.success('Application submitted successfully!');
      
      // Call the onApply callback with the result
      onApply({
        ...applicationData,
        userId: user.id,
        applicationId: result.id
      } as any);
      
      onClose();
      handleReset();
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to upload resume to storage
  const uploadResumeToStorage = async (file: File, userId: string): Promise<{ success: boolean; url?: string; error?: string }> => {
    try {
      // Preserve original filename but sanitize it for storage
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${userId}/${sanitizedFileName}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get signed URL for the uploaded file
      const { data: urlData } = await supabase.storage
        .from('user-documents')
        .createSignedUrl(filePath, 3600);

      return {
        success: true,
        url: urlData?.signedUrl || ''
      };
    } catch (error) {
      console.error('Error uploading resume:', error);
      return {
        success: false,
        error: 'Failed to upload resume'
      };
    }
  };

  // Helper to get a signed URL for an existing document in user-documents bucket
  const getSignedUrlForExistingUserDocument = async (filePath: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('user-documents')
        .createSignedUrl(filePath, 3600);
      if (error) {
        console.error('Error creating signed URL for existing document:', error);
        return null;
      }
      return data?.signedUrl ?? null;
    } catch (err) {
      console.error('Unexpected error creating signed URL:', err);
      return null;
    }
  };

  const renderModeSelectionStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" sx={{ color: theme.palette.text.primary, textAlign: 'center', fontWeight: 800, mt: 0.5 }}>
        Choose your application style
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center', maxWidth: 720, mb: 1 }}>
        Pick manual for full control, or let AI help you generate a strong application faster.
      </Typography>

      <Box
        sx={{
          width: '100%',
          maxWidth: 980,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        {/* Manual Mode Card */}
        <Box
          onClick={() => setApplicationMode('manual')}
          sx={{
            flex: 1,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: `1px solid ${applicationMode === 'manual' ? alpha(theme.palette.primary.main, 0.55) : theme.palette.divider}`,
            bgcolor: applicationMode === 'manual' ? alpha(theme.palette.primary.main, 0.06) : theme.palette.background.paper,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            transform: applicationMode === 'manual' ? 'translateY(-2px)' : 'translateY(0)',
            boxShadow: applicationMode === 'manual'
              ? `0 14px 40px ${alpha(theme.palette.primary.main, 0.22)}`
              : `0 10px 30px ${alpha('#000', 0.10)}`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 44px ${alpha('#000', 0.14)}`,
            }
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: brandGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Person sx={{ fontSize: 26, color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 800, color: theme.palette.text.primary }}>
              Manual Mode
            </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: 1 }}>
                Write everything yourself, step by step.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {['Full control', 'Personalized', 'Step-by-step'].map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.10),
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* AI Mode Card */}
        <Box
          onClick={() => setApplicationMode('ai')}
          sx={{
            flex: 1,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: `1px solid ${applicationMode === 'ai' ? alpha(theme.palette.primary.main, 0.55) : theme.palette.divider}`,
            bgcolor: applicationMode === 'ai' ? alpha(theme.palette.primary.main, 0.06) : theme.palette.background.paper,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            transform: applicationMode === 'ai' ? 'translateY(-2px)' : 'translateY(0)',
            boxShadow: applicationMode === 'ai'
              ? `0 14px 40px ${alpha(theme.palette.primary.main, 0.22)}`
              : `0 10px 30px ${alpha('#000', 0.10)}`,
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 44px ${alpha('#000', 0.14)}`,
            }
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: brandGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SmartToy sx={{ fontSize: 26, color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 800, color: theme.palette.text.primary }}>
              AI Assistant Mode
            </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: 1 }}>
                Let AI generate and suggest content.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {['AI-powered', 'Smart suggestions', 'Time-saving'].map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.10),
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {applicationMode && (
        <Box sx={{ 
          mt: 1, 
          p: 2, 
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          textAlign: 'center',
          animation: 'fadeIn 0.5s ease-in'
        }}>
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, fontWeight: 800, mb: 0.5 }}>
            {applicationMode === 'manual' ? '🎯 Manual Mode Selected' : '🤖 AI Assistant Mode Selected'}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {applicationMode === 'manual' 
              ? 'You\'ll have full control over your application. Let\'s get started!'
              : 'AI will help you create the perfect application. Let\'s begin!'
            }
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderAIAnalysisStep = () => {
    const startAIAnalysis = async () => {
      setAiGeneratingAnalysis(true);
      setAiConversation(prev => [...prev, {
        type: 'ai',
        message: "Hello! I'm your AI Application Assistant. Let me analyze this job posting to help you create the perfect application...",
        timestamp: new Date()
      }]);

      // Simulate AI analysis
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const analysis = {
        jobTitle,
        companyName,
        keySkills: ['React', 'TypeScript', 'Node.js', 'AWS', 'Agile'],
        requirements: ['3+ years experience', 'Team leadership', 'Problem solving'],
        companyCulture: 'Innovative, collaborative, growth-focused',
        salaryRange: '$80k - $120k',
        competitionLevel: 'High',
        bestApproach: 'Highlight technical expertise and leadership experience'
      };

      setAiAnalysis(analysis);
      setAiInsights([
        "This role requires strong technical leadership skills",
        "The company values innovation and collaboration",
        "Competition is high - need to stand out with specific achievements",
        "Focus on React/TypeScript experience and team management"
      ]);

      setAiConversation(prev => [...prev, {
        type: 'ai',
        message: `Perfect! I've analyzed the ${jobTitle} position at ${companyName}. This is a competitive role requiring strong technical skills. Let me help you craft an application that stands out!`,
        timestamp: new Date()
      }]);

      setAiGeneratingAnalysis(false);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 2 }}>
        {/* AI Assistant Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          p: 3, 
          bgcolor: '#f0f9ff', 
          borderRadius: 3,
          border: '1px solid #bae6fd'
        }}>
          <Box sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'pulse 2s infinite'
          }}>
            <SmartToy sx={{ fontSize: 30, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              AI Application Assistant
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              I'll analyze the job and help you create a compelling application
            </Typography>
          </Box>
        </Box>

        {/* Conversation Display */}
        {aiConversation.length > 0 && (
          <Box sx={{ 
            maxHeight: 300, 
            overflow: 'auto', 
            p: 2, 
            bgcolor: '#f8fafc', 
            borderRadius: 2,
            border: '1px solid #e5e7eb'
          }}>
            {aiConversation.map((msg, index) => (
              <Box key={index} sx={{ 
                mb: 2, 
                display: 'flex', 
                gap: 2,
                animation: 'fadeInUp 0.5s ease'
              }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: msg.type === 'ai' ? '#1976d2' : '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {msg.type === 'ai' ? <SmartToy sx={{ fontSize: 16, color: 'white' }} /> : <Person sx={{ fontSize: 16, color: 'white' }} />}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ 
                    bgcolor: msg.type === 'ai' ? '#e3f2fd' : '#f0f9ff',
                    p: 2,
                    borderRadius: 2,
                    color: '#1f2937'
                  }}>
                    {msg.message}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* AI Analysis Results */}
        {aiAnalysis && (
          <Box sx={{ 
            p: 4, 
            bgcolor: 'white', 
            borderRadius: 3, 
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#1f2937', fontWeight: 700 }}>
              📊 Job Analysis Results
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 1 }}>Key Skills Required</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {aiAnalysis.keySkills.map((skill: string, index: number) => (
                    <Box key={index} sx={{
                      px: 2,
                      py: 0.5,
                      bgcolor: '#e3f2fd',
                      color: '#1976d2',
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      fontWeight: 600
                    }}>
                      {skill}
                    </Box>
                  ))}
                </Box>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 1 }}>Competition Level</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 100, 
                    height: 8, 
                    bgcolor: '#e5e7eb', 
                    borderRadius: 4,
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ 
                      width: '85%', 
                      height: '100%', 
                      bgcolor: '#ef4444',
                      borderRadius: 4
                    }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 600 }}>
                    High
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 1 }}>AI Insights</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {aiInsights.map((insight, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  p: 2,
                  bgcolor: '#f0f9ff',
                  borderRadius: 2,
                  border: '1px solid #bae6fd'
                }}>
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    bgcolor: '#1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Typography sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 'bold' }}>✓</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#1f2937' }}>
                    {insight}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Start Analysis Button */}
        {!aiAnalysis && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Button
              onClick={startAIAnalysis}
              disabled={aiGeneratingAnalysis}
              variant="contained"
              size="large"
              startIcon={aiGeneratingAnalysis ? <CircularProgress size={20} color="inherit" /> : <SmartToy />}
              sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)'
                },
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              {aiGeneratingAnalysis ? 'Analyzing Job Posting...' : 'Start AI Analysis'}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const renderPersonalInfoStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#1f2937' }}>
          Tell us about yourself
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AutoFixHigh />}
          onClick={generatePersonalInfo}
          sx={{
            borderColor: '#3b82f6',
            color: '#3b82f6',
            '&:hover': {
              borderColor: '#2563eb',
              backgroundColor: '#eff6ff',
            }
          }}
        >
          Generate
        </Button>
      </Box>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
        <TextField
          label="First Name"
          value={applicationData.personalInfo.first_name}
          onChange={(e) => setApplicationData(prev => ({
            ...prev,
            personalInfo: { ...prev.personalInfo, first_name: e.target.value }
          }))}
          fullWidth
          required
          disabled={loadingUserProfile}
          InputProps={{
            startAdornment: loadingUserProfile ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : (
              <Person sx={{ mr: 1, color: '#6b7280' }} />
            )
          }}
        />
        <TextField
          label="Last Name"
          value={applicationData.personalInfo.last_name}
          onChange={(e) => setApplicationData(prev => ({
            ...prev,
            personalInfo: { ...prev.personalInfo, last_name: e.target.value }
          }))}
          fullWidth
          required
          disabled={loadingUserProfile}
          InputProps={{
            startAdornment: loadingUserProfile ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : (
              <Person sx={{ mr: 1, color: '#6b7280' }} />
            )
          }}
        />
        <TextField
          label="Email"
          type="email"
          value={applicationData.personalInfo.email}
          onChange={(e) => setApplicationData(prev => ({
            ...prev,
            personalInfo: { ...prev.personalInfo, email: e.target.value }
          }))}
          fullWidth
          required
          disabled={loadingUserProfile}
          InputProps={{
            startAdornment: loadingUserProfile ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : (
              <Email sx={{ mr: 1, color: '#6b7280' }} />
            )
          }}
        />
        <TextField
          label="Phone Number"
          value={applicationData.personalInfo.phone}
          onChange={(e) => setApplicationData(prev => ({
            ...prev,
            personalInfo: { ...prev.personalInfo, phone: e.target.value }
          }))}
          fullWidth
          disabled={loadingUserProfile}
          InputProps={{
            startAdornment: loadingUserProfile ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : (
              <Phone sx={{ mr: 1, color: '#6b7280' }} />
            )
          }}
        />
        <TextField
          label="LinkedIn Profile (Optional)"
          value={applicationData.personalInfo.linkedin}
          onChange={(e) => setApplicationData(prev => ({
            ...prev,
            personalInfo: { ...prev.personalInfo, linkedin: e.target.value }
          }))}
          fullWidth
          disabled={loadingUserProfile}
          InputProps={{
            startAdornment: loadingUserProfile ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : (
              <Work sx={{ mr: 1, color: '#6b7280' }} />
            )
          }}
        />
      </Box>
      
      <TextField
        label="Portfolio/Website (Optional)"
        value={applicationData.personalInfo.portfolio}
        onChange={(e) => {
          setApplicationData(prev => ({
            ...prev,
            personalInfo: { ...prev.personalInfo, portfolio: e.target.value }
          }));
        }}
        fullWidth
        disabled={loadingUserProfile}
        InputProps={{
          startAdornment: loadingUserProfile ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <Work sx={{ mr: 1, color: '#6b7280' }} />
          )
        }}
      />
    </Box>
  );

  const renderExperienceStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#1f2937' }}>
          Your Professional Background
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadUserProfileData}
            disabled={loadingUserProfile}
            sx={{
              borderColor: '#10b981',
              color: '#10b981',
              '&:hover': {
                borderColor: '#059669',
                backgroundColor: '#ecfdf5',
              },
            }}
          >
            {loadingUserProfile ? 'Loading...' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AutoFixHigh />}
            onClick={generateProfessionalBackground}
            sx={{
              borderColor: '#3b82f6',
              color: '#3b82f6',
              '&:hover': {
                borderColor: '#2563eb',
                backgroundColor: '#eff6ff',
              }
            }}
          >
            Generate
          </Button>
        </Box>
      </Box>
      
      <TextField
        label="Work Experience"
        multiline
        rows={4}
        value={applicationData.experience}
        onChange={(e) => setApplicationData(prev => ({ ...prev, experience: e.target.value }))}
        placeholder="Describe your relevant work experience, including key achievements and responsibilities..."
        fullWidth
        InputProps={{
          startAdornment: <Work sx={{ mr: 1, color: '#6b7280', alignSelf: 'flex-start', mt: 1 }} />
        }}
      />
      
      <TextField
        label="Education"
        multiline
        rows={3}
        value={applicationData.education}
        onChange={(e) => setApplicationData(prev => ({ ...prev, education: e.target.value }))}
        placeholder="Your educational background, degrees, certifications..."
        fullWidth
        InputProps={{
          startAdornment: <School sx={{ mr: 1, color: '#6b7280', alignSelf: 'flex-start', mt: 1 }} />
        }}
      />
      
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
          Key Skills
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {applicationData.skills.map((skill, index) => (
            <Chip
              key={index}
              label={skill}
              onDelete={() => removeSkill(skill)}
              color="primary"
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
        <TextField
          label="Add a skill"
          size="small"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              addSkill((e.target as HTMLInputElement).value);
              (e.target as HTMLInputElement).value = '';
            }
          }}
          fullWidth
        />
      </Box>
    </Box>
  );

  const renderAICoverLetterStep = () => {
    const generateAICoverLetter = async () => {
      setAiGenerating(true);
      setAiConversation(prev => [...prev, {
        type: 'ai',
        message: "Now let me create a compelling cover letter tailored specifically for this role. I'll analyze your experience and craft something that will make you stand out...",
        timestamp: new Date()
      }]);

      // Simulate AI cover letter generation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const generatedCoverLetter = `Dear Hiring Manager,

I am writing to express my strong interest in the ${jobTitle} position at ${companyName}. With my extensive experience in React, TypeScript, and team leadership, I am confident that I would be a valuable addition to your innovative team.

In my current role, I have successfully led development teams of 5+ engineers, implementing scalable solutions that improved system performance by 40%. My expertise in modern JavaScript frameworks, combined with my passion for collaborative problem-solving, aligns perfectly with ${companyName}'s mission of driving technological innovation.

I am particularly drawn to this opportunity because of ${companyName}'s reputation for fostering growth and innovation. I believe my technical skills and leadership experience would contribute significantly to your team's continued success.

Thank you for considering my application. I look forward to the opportunity to discuss how my background and enthusiasm can contribute to ${companyName}'s continued growth.

Best regards,
[Your Name]`;

      setAiGeneratedContent((prev: any) => ({ ...prev, coverLetter: generatedCoverLetter }));
      setApplicationData(prev => ({ ...prev, coverLetter: generatedCoverLetter }));

      setAiConversation(prev => [...prev, {
        type: 'ai',
        message: "Perfect! I've generated a personalized cover letter that highlights your key strengths and aligns with the job requirements. The letter emphasizes your technical expertise and leadership experience, which are exactly what they're looking for.",
        timestamp: new Date()
      }]);

      setAiGenerating(false);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 2 }}>
        {/* AI Assistant Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          p: 3, 
          bgcolor: '#f0f9ff', 
          borderRadius: 3,
          border: '1px solid #bae6fd'
        }}>
          <Box sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: aiGenerating ? 'pulse 1s infinite' : 'none'
          }}>
            <SmartToy sx={{ fontSize: 30, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              AI Cover Letter Generator
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              I'll create a personalized cover letter that matches this job perfectly
            </Typography>
          </Box>
        </Box>

        {/* AI Strategy */}
        <Box sx={{ 
          p: 4, 
          bgcolor: 'white', 
          borderRadius: 3, 
          border: '1px solid #e5e7eb',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
          <Typography variant="h6" sx={{ mb: 3, color: '#1f2937', fontWeight: 700 }}>
            🎯 AI Strategy for Your Cover Letter
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 2 }}>Key Points to Highlight</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  "Technical leadership experience",
                  "React/TypeScript expertise", 
                  "Team management skills",
                  "Problem-solving achievements"
                ].map((point, index) => (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1.5,
                    bgcolor: '#f0f9ff',
                    borderRadius: 2,
                    border: '1px solid #bae6fd'
                  }}>
                    <Box sx={{ 
                      width: 16, 
                      height: 16, 
                      borderRadius: '50%', 
                      bgcolor: '#1976d2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 'bold' }}>✓</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#1f2937' }}>
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 2 }}>Tone & Style</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                  <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 600, mb: 0.5 }}>
                    Professional & Confident
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                    Emphasizing achievements and value proposition
                  </Typography>
                </Box>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                  <Typography variant="body2" sx={{ color: '#1f2937', fontWeight: 600, mb: 0.5 }}>
                    Company-Specific
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                    Tailored to {companyName}'s culture and values
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Generate Button */}
        {!aiGeneratedContent.coverLetter && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Button
              onClick={generateAICoverLetter}
              disabled={aiGenerating}
              variant="contained"
              size="large"
              startIcon={aiGenerating ? <CircularProgress size={20} color="inherit" /> : <SmartToy />}
              sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)'
                },
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              {aiGenerating ? 'Generating Cover Letter...' : 'Generate AI Cover Letter'}
            </Button>
          </Box>
        )}

        {/* Generated Cover Letter */}
        {aiGeneratedContent.coverLetter && (
          <Box sx={{ 
            p: 4, 
            bgcolor: 'white', 
            borderRadius: 3, 
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircle sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 700 }}>
                  ✨ AI-Generated Cover Letter
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Personalized and optimized for this position
                </Typography>
              </Box>
            </Box>
            
            <TextField
              multiline
              rows={12}
              fullWidth
              value={aiGeneratedContent.coverLetter}
              onChange={(e) => {
                setAiGeneratedContent((prev: any) => ({ ...prev, coverLetter: e.target.value }));
                setApplicationData(prev => ({ ...prev, coverLetter: e.target.value }));
              }}
              placeholder="Your AI-generated cover letter will appear here..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#f8fafc',
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: '#f0f9ff'
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                    boxShadow: '0 0 0 2px #3b82f6'
                  }
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                  lineHeight: 1.6,
                  padding: '16px'
                }
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={generateAICoverLetter}
                disabled={aiGenerating}
                sx={{ 
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    bgcolor: '#f0f9ff'
                  }
                }}
              >
                Regenerate
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircle />}
                sx={{
                  bgcolor: '#10b981',
                  '&:hover': { bgcolor: '#059669' }
                }}
              >
                Use This Letter
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderCoverLetterStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: '#1f2937' }}>
          Cover Letter
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SmartToy />}
          onClick={generateAICoverLetter}
          disabled={aiGenerating}
          sx={{ 
            borderColor: '#3b82f6',
            color: '#3b82f6',
            '&:hover': {
              borderColor: '#2563eb',
              backgroundColor: '#eff6ff'
            }
          }}
        >
          {aiGenerating ? 'Generating...' : 'AI Assistant'}
        </Button>
      </Box>

      {aiGenerating && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            AI is analyzing the job requirements and generating suggestions...
          </Typography>
        </Box>
      )}

      {showAiGuide && aiSuggestions.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Lightbulb sx={{ color: '#0ea5e9', mr: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0c4a6e' }}>
                AI Suggestions
              </Typography>
            </Box>
            <List dense>
              {aiSuggestions.map((suggestion, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon>
                    <Star sx={{ fontSize: 16, color: '#0ea5e9' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={suggestion}
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                  />
                  <Button
                    size="small"
                    onClick={() => applyAISuggestion(suggestion)}
                    sx={{ ml: 1, minWidth: 'auto' }}
                  >
                    Apply
                  </Button>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      <TextField
        label="Write your cover letter"
        multiline
        rows={8}
        fullWidth
        value={applicationData.coverLetter}
        onChange={(e) => setApplicationData(prev => ({ ...prev, coverLetter: e.target.value }))}
        placeholder="Dear Hiring Manager,

I am writing to express my strong interest in the [Job Title] position at [Company Name]. 

[Your personalized content here]

Thank you for considering my application.

Best regards,
[Your Name]"
        InputProps={{
          startAdornment: <Description sx={{ mr: 1, color: '#6b7280', alignSelf: 'flex-start', mt: 1 }} />
        }}
      />
    </Box>
  );

  const renderDocumentsStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#1f2937' }}>
        Upload Documents
      </Typography>
      
      {/* Smart CV Selection */}
      <Paper sx={{ 
        p: 3, 
        border: '1px solid #d1d5db', 
        bgcolor: '#f9fafb',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: '#10b981', 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Description sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 600 }}>
                CV/Resume *
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose from your library or upload a new file
              </Typography>
            </Box>
          </Box>
          {(applicationData.resume || selectedCvFromLibrary || applicationData.selectedDocuments.length > 0) && (
            <Chip
              label="Selected"
              color="success"
              size="small"
              icon={<CheckCircle />}
            />
          )}
        </Box>

        {/* Current CV Selection */}
        {(applicationData.resume || selectedCvFromLibrary || applicationData.selectedDocuments.length > 0) ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Card sx={{ 
              p: 2, 
              bgcolor: 'white', 
              borderRadius: 2, 
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-1px)',
                transition: 'all 0.2s ease'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <Box sx={{ 
                    p: 1, 
                    bgcolor: '#10b981', 
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Description sx={{ color: 'white' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                      {applicationData.resume ? applicationData.resume.name : 
                       selectedCvFromLibrary ? selectedCvFromLibrary.file_name :
                       applicationData.selectedDocuments.length > 0 ? `${applicationData.selectedDocuments.length} document(s) selected` : 'No document'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {applicationData.resume 
                          ? `${(applicationData.resume.size / 1024 / 1024).toFixed(2)} MB`
                          : selectedCvFromLibrary ? formatFileSize(selectedCvFromLibrary.file_size) : 
                            applicationData.selectedDocuments.length > 0 ? `${applicationData.selectedDocuments.reduce((total, doc) => total + doc.document.file_size, 0) / 1024 / 1024} MB total` : ''
                        }
                      </Typography>
                      <Typography variant="caption" color="text.secondary">•</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {applicationData.resume ? 'Uploaded File' : 
                         selectedCvFromLibrary ? 'From Library' :
                         applicationData.selectedDocuments.length > 0 ? 'From Library' : 'No source'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Refresh />}
                    onClick={() => setShowCvSelector(!showCvSelector)}
                    sx={{ 
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Change
                  </Button>
                  <IconButton 
                    size="small" 
                    onClick={handleRemoveCv}
                    sx={{ 
                      color: '#ef4444',
                      '&:hover': {
                        bgcolor: '#fef2f2'
                      }
                    }}
                  >
                    <Delete sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            </Card>
          </motion.div>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose how you'd like to provide your CV:
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {/* Upload New File */}
              <Card
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid #e5e7eb',
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    bgcolor: '#f8fafc',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={handleUploadNewCv}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#3b82f6', 
                    borderRadius: '50%', 
                    display: 'inline-flex',
                    mb: 1
                  }}>
                    <Upload sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                    Upload New File
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF, DOC, DOCX up to 5MB
                  </Typography>
                </Box>
              </Card>

              {/* Choose from Library */}
              <Card
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid #e5e7eb',
                  borderRadius: 2,
                  bgcolor: 'white',
                  visibility: hasSession ? 'visible' : 'hidden',
                  pointerEvents: hasSession ? 'auto' : 'none',
                  '&:hover': {
                    borderColor: '#10b981',
                    bgcolor: '#f0fdf4',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={() => hasSession && setShowCvSelector(!showCvSelector)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#10b981', 
                    borderRadius: '50%', 
                    display: 'inline-flex',
                    mb: 1
                  }}>
                    <FolderOpen sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                    Choose from Library
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select from your uploaded documents
                  </Typography>
                </Box>
              </Card>
            </Box>
          </Box>
        )}

        {/* CV Library Selector */}
        {showCvSelector && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ 
              mt: 2,
              border: '1px solid #e5e7eb', 
              borderRadius: 2,
              bgcolor: 'white',
              overflow: 'hidden'
            }}>
              <Box sx={{ p: 2, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                  Select CV from Your Library
                </Typography>
                <Button size="small" variant="outlined" onClick={loadUserDocuments} startIcon={<Refresh />} sx={{ textTransform: 'none' }}>
                  Refresh
                </Button>
              </Box>
              
              {loadingDocuments ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CircularProgress size={24} sx={{ color: '#10b981' }} />
                  <Typography variant="body2" sx={{ mt: 1, color: '#6b7280' }}>
                    Loading documents...
                  </Typography>
                </Box>
              ) : userDocuments.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: '#f3f4f6', 
                    borderRadius: '50%', 
                    display: 'inline-flex',
                    mb: 1
                  }}>
                    <FolderOpen sx={{ fontSize: 24, color: '#9ca3af' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    No documents found. Upload some documents first.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List sx={{ p: 0 }}>
                    {userDocuments.map((doc) => (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ListItem
                          component="div"
                          onClick={() => handleSelectCvFromLibrary(doc)}
                          sx={{
                            borderBottom: '1px solid #f3f4f6',
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: '#f0fdf4'
                            },
                            bgcolor: selectedCvFromLibrary?.id === doc.id ? '#f0fdf4' : 'transparent',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <ListItemIcon>
                            <Box sx={{ 
                              p: 1, 
                              bgcolor: selectedCvFromLibrary?.id === doc.id ? '#10b981' : '#f3f4f6', 
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {getFileIcon(doc.file_type)}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1f2937' }}>
                                  {doc.file_name}
                                </Typography>
                                {selectedCvFromLibrary?.id === doc.id && (
                                  <Box sx={{ 
                                    p: 0.5, 
                                    bgcolor: '#10b981', 
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
                                  </Box>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(doc.file_size)} • {doc.document_type}
                                </Typography>
                                {doc.description && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {doc.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      </motion.div>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          </motion.div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={(e) => handleFileUpload(e, 'resume')}
          style={{ display: 'none' }}
        />
      </Paper>

      {/* Smart Additional Documents Selection */}
      <Paper sx={{ 
        p: 3, 
        border: '1px solid #d1d5db', 
        bgcolor: '#f9fafb',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              bgcolor: '#8b5cf6', 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AttachFile sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 600 }}>
                Additional Documents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose from your library or upload new files
              </Typography>
            </Box>
          </Box>
          {(applicationData.additionalDocuments.length > 0 || selectedAdditionalDocsFromLibrary.length > 0) && (
            <Chip
              label={`${applicationData.additionalDocuments.length + selectedAdditionalDocsFromLibrary.length} Selected`}
              color="primary"
              size="small"
              icon={<CheckCircle />}
            />
          )}
        </Box>

        {/* Current Additional Documents */}
        {(applicationData.additionalDocuments.length > 0 || selectedAdditionalDocsFromLibrary.length > 0) ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Uploaded Files */}
              {applicationData.additionalDocuments.map((file, index) => (
                <Card key={`uploaded-${index}`} sx={{ 
                  p: 2, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease'
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Box sx={{ 
                        p: 1, 
                        bgcolor: '#3b82f6', 
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Description sx={{ color: 'white' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(file.size / 1024 / 1024).toFixed(2)} MB • Uploaded File
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={() => removeFile(index, 'additional')}
                      sx={{ 
                        color: '#ef4444',
                        '&:hover': {
                          bgcolor: '#fef2f2'
                        }
                      }}
                    >
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Card>
              ))}

              {/* Selected from Library */}
              {selectedAdditionalDocsFromLibrary.map((doc) => (
                <Card key={`library-${doc.id}`} sx={{ 
                  p: 2, 
                  bgcolor: 'white', 
                  borderRadius: 2, 
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease'
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Box sx={{ 
                        p: 1, 
                        bgcolor: '#8b5cf6', 
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {getFileIcon(doc.file_type)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                          {doc.file_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(doc.file_size)} • From Library
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleRemoveAdditionalDoc(doc.id)}
                      sx={{ 
                        color: '#ef4444',
                        '&:hover': {
                          bgcolor: '#fef2f2'
                        }
                      }}
                    >
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Card>
              ))}

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={() => setShowAdditionalDocsSelector(!showAdditionalDocsSelector)}
                  sx={{ 
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Add More
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Delete />}
                  onClick={handleRemoveAllAdditionalDocs}
                  sx={{ 
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 500,
                    color: '#ef4444',
                    borderColor: '#ef4444',
                    '&:hover': {
                      bgcolor: '#fef2f2',
                      borderColor: '#dc2626'
                    }
                  }}
                >
                  Remove All
                </Button>
              </Box>
            </Box>
          </motion.div>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose how you'd like to add additional documents:
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {/* Upload New Files */}
              <Card
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid #e5e7eb',
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    bgcolor: '#f8fafc',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={handleUploadNewAdditionalDocs}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#3b82f6', 
                    borderRadius: '50%', 
                    display: 'inline-flex',
                    mb: 1
                  }}>
                    <Upload sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                    Upload New Files
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF, DOC, DOCX, Images up to 5MB each
                  </Typography>
                </Box>
              </Card>

              {/* Choose from Library */}
              <Card
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid #e5e7eb',
                  borderRadius: 2,
                  bgcolor: 'white',
                  visibility: hasSession ? 'visible' : 'hidden',
                  pointerEvents: hasSession ? 'auto' : 'none',
                  '&:hover': {
                    borderColor: '#8b5cf6',
                    bgcolor: '#faf5ff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={() => hasSession && setShowAdditionalDocsSelector(!showAdditionalDocsSelector)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#8b5cf6', 
                    borderRadius: '50%', 
                    display: 'inline-flex',
                    mb: 1
                  }}>
                    <FolderOpen sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                    Choose from Library
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select from your uploaded documents
                  </Typography>
                </Box>
              </Card>
            </Box>
          </Box>
        )}

        {/* Additional Documents Library Selector */}
        {showAdditionalDocsSelector && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ 
              mt: 2,
              border: '1px solid #e5e7eb', 
              borderRadius: 2,
              bgcolor: 'white',
              overflow: 'hidden'
            }}>
              <Box sx={{ p: 2, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                  Select Additional Documents from Your Library
                </Typography>
              </Box>
              
              {loadingDocuments ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <CircularProgress size={24} sx={{ color: '#8b5cf6' }} />
                  <Typography variant="body2" sx={{ mt: 1, color: '#6b7280' }}>
                    Loading documents...
                  </Typography>
                </Box>
              ) : userDocuments.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: '#f3f4f6', 
                    borderRadius: '50%', 
                    display: 'inline-flex',
                    mb: 1
                  }}>
                    <FolderOpen sx={{ fontSize: 24, color: '#9ca3af' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    No documents found. Upload some documents first.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List sx={{ p: 0 }}>
                    {userDocuments.map((doc) => {
                      const isSelected = selectedAdditionalDocsFromLibrary.some(selected => selected.id === doc.id);
                      return (
                        <motion.div
                          key={doc.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ListItem
                            component="div"
                            onClick={() => handleSelectAdditionalDocFromLibrary(doc)}
                            sx={{
                              borderBottom: '1px solid #f3f4f6',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: '#faf5ff'
                              },
                              bgcolor: isSelected ? '#faf5ff' : 'transparent',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <ListItemIcon>
                              <Box sx={{ 
                                p: 1, 
                                bgcolor: isSelected ? '#8b5cf6' : '#f3f4f6', 
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {getFileIcon(doc.file_type)}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#1f2937' }}>
                                    {doc.file_name}
                                  </Typography>
                                  {isSelected && (
                                    <Box sx={{ 
                                      p: 0.5, 
                                      bgcolor: '#8b5cf6', 
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}>
                                      <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
                                    </Box>
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatFileSize(doc.file_size)} • {doc.document_type}
                                  </Typography>
                                  {doc.description && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {doc.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        </motion.div>
                      );
                    })}
                  </List>
                </Box>
              )}
            </Box>
          </motion.div>
        )}

        <input
          ref={additionalFilesRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          onChange={(e) => handleFileUpload(e, 'additional')}
          style={{ display: 'none' }}
        />
      </Paper>

      {hasSession && (
        <>
          {/* Smart Document Selection */}
          <Paper sx={{ 
            p: 3, 
            border: '1px solid #e5e7eb', 
            bgcolor: '#fafbfc',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: '#3b82f6', 
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FolderOpen sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 600 }}>
                    Smart Document Selection
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose documents and specify their purpose for this application
                  </Typography>
                </Box>
              </Box>
              <Button
                variant={showDocumentSelector ? "contained" : "outlined"}
                startIcon={<FolderOpen />}
                onClick={() => setShowDocumentSelector(!showDocumentSelector)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                }}
              >
                {showDocumentSelector ? 'Hide Library' : 'Browse Library'}
              </Button>
            </Box>

        {/* Selected Documents with Purpose */}
        {applicationData.selectedDocuments.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, color: '#1f2937', fontWeight: 600 }}>
              Selected Documents ({applicationData.selectedDocuments.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {applicationData.selectedDocuments.map((selectedDoc) => {
                const purposeConfig = getPurposeConfig(selectedDoc.purpose);
                return (
                  <motion.div
                    key={selectedDoc.document.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card sx={{ 
                      p: 2, 
                      bgcolor: 'white', 
                      borderRadius: 2, 
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s ease'
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          <Box sx={{ 
                            p: 1, 
                            bgcolor: purposeConfig.required ? '#fef3c7' : '#f3f4f6', 
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {getFileIcon(selectedDoc.document.file_type)}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                              {selectedDoc.document.file_name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(selectedDoc.document.file_size)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">•</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {selectedDoc.document.document_type}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* Purpose Selector */}
                          {editingDocumentPurpose === selectedDoc.document.id ? (
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <Select
                                value={selectedDoc.purpose}
                                onChange={(e: any) => handleUpdateDocumentPurpose(selectedDoc.document.id, e.target.value)}
                                onBlur={() => setEditingDocumentPurpose(null)}
                                autoFocus
                                sx={{ fontSize: '0.875rem' }}
                              >
                                {DOCUMENT_PURPOSES.map((purpose) => (
                                  <MenuItem key={purpose.value} value={purpose.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <span>{purpose.icon}</span>
                                      <span>{purpose.label}</span>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span>{purposeConfig.icon}</span>
                                  <span>{purposeConfig.label}</span>
                                </Box>
                              }
                              onClick={() => setEditingDocumentPurpose(selectedDoc.document.id)}
                              color={purposeConfig.required ? "warning" : "primary"}
                              variant={purposeConfig.required ? "filled" : "outlined"}
                              size="small"
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: purposeConfig.required ? '#f59e0b' : '#3b82f6',
                                  color: 'white'
                                }
                              }}
                            />
                          )}
                          
                          <IconButton 
                            size="small" 
                            onClick={() => handleRemoveSelectedDocument(selectedDoc.document.id)}
                            sx={{ 
                              color: '#ef4444',
                              '&:hover': {
                                bgcolor: '#fef2f2'
                              }
                            }}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                );
              })}
            </Box>
          </Box>
        )}

            {/* Document Library */}
            {showDocumentSelector && (
              <Box sx={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: 2,
                bgcolor: 'white',
                overflow: 'hidden'
              }}>
            {loadingDocuments ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
                <Typography variant="body2" sx={{ mt: 2, color: '#6b7280' }}>
                  Loading your documents...
                </Typography>
              </Box>
            ) : userDocuments.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: '#f3f4f6', 
                  borderRadius: '50%', 
                  display: 'inline-flex',
                  mb: 2
                }}>
                  <FolderOpen sx={{ fontSize: 32, color: '#9ca3af' }} />
                </Box>
                <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                  No documents found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload some documents first to use this feature
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <Box sx={{ p: 2, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <Typography variant="subtitle2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                    Available Documents ({userDocuments.length})
                  </Typography>
                </Box>
                <List sx={{ p: 0 }}>
                  {userDocuments.map((doc) => {
                    const isSelected = applicationData.selectedDocuments.some(selected => selected.document.id === doc.id);
                    return (
                      <motion.div
                        key={doc.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ListItem
                          component="div"
                          onClick={() => handleSelectDocument(doc, 'other')}
                          sx={{
                            borderBottom: '1px solid #f3f4f6',
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: '#f9fafb'
                            },
                            bgcolor: isSelected ? '#eff6ff' : 'transparent',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <ListItemIcon>
                            <Box sx={{ 
                              p: 1, 
                              bgcolor: isSelected ? '#3b82f6' : '#f3f4f6', 
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {getFileIcon(doc.file_type)}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1f2937' }}>
                                  {doc.file_name}
                                </Typography>
                                {isSelected && (
                                  <Box sx={{ 
                                    p: 0.5, 
                                    bgcolor: '#10b981', 
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
                                  </Box>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(doc.file_size)} • {doc.document_type}
                                </Typography>
                                {doc.description && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {doc.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      </motion.div>
                    );
                  })}
                </List>
              </Box>
            )}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );

  const renderReviewStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#1f2937' }}>
        Review Your Application
      </Typography>
      
      <Card sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          Personal Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Name</Typography>
            <Typography variant="body1">
              {applicationData.personalInfo.first_name && applicationData.personalInfo.last_name 
                ? `${applicationData.personalInfo.first_name} ${applicationData.personalInfo.last_name}`.trim()
                : applicationData.personalInfo.first_name || applicationData.personalInfo.last_name || 'Not provided'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Email</Typography>
            <Typography variant="body1">{applicationData.personalInfo.email || 'Not provided'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Phone</Typography>
            <Typography variant="body1">{applicationData.personalInfo.phone || 'Not provided'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">LinkedIn</Typography>
            <Typography variant="body1">{applicationData.personalInfo.linkedin || 'Not provided'}</Typography>
          </Box>
        </Box>
      </Card>

      <Card sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          Documents
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle sx={{ mr: 1, color: (applicationData.resume || selectedCvFromLibrary || applicationData.selectedDocuments.length > 0) ? '#10b981' : '#ef4444' }} />
            <Typography variant="body2">
              Resume: {(applicationData.resume || selectedCvFromLibrary || applicationData.selectedDocuments.length > 0)
                ? (applicationData.resume ? applicationData.resume.name : 
                   selectedCvFromLibrary ? selectedCvFromLibrary.file_name : 
                   `${applicationData.selectedDocuments.length} document(s) selected`)
                : 'Not selected'
              }
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle sx={{ mr: 1, color: (applicationData.additionalDocuments.length > 0 || selectedAdditionalDocsFromLibrary.length > 0) ? '#10b981' : '#6b7280' }} />
            <Typography variant="body2">
              Additional Documents: {applicationData.additionalDocuments.length + selectedAdditionalDocsFromLibrary.length} file(s)
            </Typography>
          </Box>
          
          {/* Show additional documents details */}
          {(applicationData.additionalDocuments.length > 0 || selectedAdditionalDocsFromLibrary.length > 0) && (
            <Box sx={{ mt: 2, pl: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block', fontWeight: 600 }}>
                Additional Documents:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Uploaded Files */}
                {applicationData.additionalDocuments.map((file, index) => (
                  <Box key={`uploaded-${index}`} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    p: 1.5,
                    bgcolor: '#f8fafc',
                    borderRadius: 1.5,
                    border: '1px solid #e5e7eb'
                  }}>
                    <Box sx={{ 
                      p: 0.5, 
                      bgcolor: '#3b82f6', 
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Description sx={{ color: 'white', fontSize: 16 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#1f2937' }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {(file.size / 1024 / 1024).toFixed(2)} MB • Uploaded File
                      </Typography>
                    </Box>
                    <Chip 
                      label="Uploaded" 
                      size="small" 
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                ))}
                
                {/* Selected from Library */}
                {selectedAdditionalDocsFromLibrary.map((doc) => (
                  <Box key={`library-${doc.id}`} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    p: 1.5,
                    bgcolor: '#faf5ff',
                    borderRadius: 1.5,
                    border: '1px solid #e5e7eb'
                  }}>
                    <Box sx={{ 
                      p: 0.5, 
                      bgcolor: '#8b5cf6', 
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {getFileIcon(doc.file_type)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#1f2937' }}>
                        {doc.file_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatFileSize(doc.file_size)} • From Library
                      </Typography>
                    </Box>
                    <Chip 
                      label="Library" 
                      size="small" 
                      color="secondary"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle sx={{ mr: 1, color: applicationData.selectedDocuments.length > 0 ? '#10b981' : '#6b7280' }} />
            <Typography variant="body2">
              Selected from Library: {applicationData.selectedDocuments.length} document(s)
            </Typography>
          </Box>
          
          {/* Show selected documents with purposes */}
          {applicationData.selectedDocuments.length > 0 && (
            <Box sx={{ mt: 2, pl: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block', fontWeight: 600 }}>
                Selected Documents with Purpose:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {applicationData.selectedDocuments.map((selectedDoc) => {
                  const purposeConfig = getPurposeConfig(selectedDoc.purpose);
                  return (
                    <Box key={selectedDoc.document.id} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5, 
                      p: 1.5,
                      bgcolor: purposeConfig.required ? '#fef3c7' : '#f9fafb',
                      borderRadius: 1.5,
                      border: `1px solid ${purposeConfig.required ? '#f59e0b' : '#e5e7eb'}`
                    }}>
                      <Box sx={{ 
                        p: 0.5, 
                        bgcolor: purposeConfig.required ? '#f59e0b' : '#3b82f6', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {getFileIcon(selectedDoc.document.file_type)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#1f2937' }}>
                          {selectedDoc.document.file_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatFileSize(selectedDoc.document.file_size)} • {selectedDoc.document.document_type}
                        </Typography>
                      </Box>
                      <Chip 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{purposeConfig.icon}</span>
                            <span>{purposeConfig.label}</span>
                          </Box>
                        }
                        color={purposeConfig.required ? "warning" : "primary"}
                        variant={purposeConfig.required ? "filled" : "outlined"}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Card>

      {applicationData.coverLetter && (
        <Card sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
            Cover Letter Preview
          </Typography>
          <Typography variant="body2" sx={{ 
            whiteSpace: 'pre-wrap',
            maxHeight: 200,
            overflow: 'auto',
            bgcolor: '#f9fafb',
            p: 2,
            borderRadius: 1
          }}>
            {applicationData.coverLetter}
          </Typography>
        </Card>
      )}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={isMaximized ? "lg" : "md"}
        fullWidth
        fullScreen={isMaximized}
        PaperProps={{
          sx: {
            borderRadius: isMaximized ? 0 : 3,
            maxHeight: isMaximized ? '100vh' : '90vh',
            bgcolor: 'white',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: isMaximized ? 'none' : '0 24px 80px rgba(0,0,0,0.25)',
          }
        }}
      >
      <DialogTitle
        sx={{
          color: theme.palette.text.primary,
          textAlign: 'center',
          py: 1.5,
          px: 2,
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Apply for {jobTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.2 }}>
              {companyName}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => setIsMaximized(!isMaximized)}
              sx={{
                color: theme.palette.text.primary,
                bgcolor: alpha(theme.palette.text.primary, 0.06),
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.10) },
              }}
            >
              {isMaximized ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
            <IconButton
              onClick={onClose}
              sx={{
                color: theme.palette.text.primary,
                bgcolor: alpha(theme.palette.text.primary, 0.06),
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.10) },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          overflow: 'auto',
          flex: 1,
          bgcolor: theme.palette.background.default,
          background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${theme.palette.background.default} 55%)`,
        }}
      >
        <Box sx={{ p: 3, pb: 12 }}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: alpha(theme.palette.background.paper, 0.92),
              backdropFilter: 'blur(10px)',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              pb: 1.5,
              mb: 3,
              borderRadius: 2,
              px: 1,
            }}
          >
            <Tabs
              value={activeStep}
              onChange={(_, v) => handleStepClick(v)}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              sx={{
                minHeight: 44,
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: 999,
                  background: brandGradient,
                },
              }}
            >
              {steps.map((label, index) => (
                <Tab
                  key={label}
                  label={label}
                  sx={{
                    minHeight: 44,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    color: theme.palette.text.secondary,
                    '&.Mui-selected': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeStep === 0 && renderModeSelectionStep()}
              {activeStep === 1 && (applicationMode === 'ai' ? renderAIAnalysisStep() : renderPersonalInfoStep())}
              {activeStep === 2 && (applicationMode === 'ai' ? renderPersonalInfoStep() : renderExperienceStep())}
              {activeStep === 3 && (applicationMode === 'ai' ? renderExperienceStep() : renderCoverLetterStep())}
              {activeStep === 4 && (applicationMode === 'ai' ? renderAICoverLetterStep() : renderDocumentsStep())}
              {activeStep === 5 && (applicationMode === 'ai' ? renderDocumentsStep() : renderReviewStep())}
              {activeStep === 6 && renderReviewStep()}
            </motion.div>
          </AnimatePresence>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 2,
        bgcolor: alpha(theme.palette.background.paper, 0.94),
        backdropFilter: 'blur(10px)',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        position: 'sticky',
        bottom: 0,
        zIndex: 3,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{ color: '#6b7280' }}
          >
            Back
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleReset}
              sx={{ color: '#6b7280' }}
            >
              Reset
            </Button>
            
            {activeStep === 0 ? (
              <Button
                onClick={handleNext}
                variant="contained"
                disabled={!applicationMode}
                sx={{
                  background: brandGradient,
                  '&:hover': { 
                    background: brandGradient,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.35)}`
                  },
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                  transition: 'all 0.3s ease'
                }}
              >
                Continue with {applicationMode === 'manual' ? 'Manual Mode' : 'AI Assistant'}
              </Button>
            ) : activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onMouseUp={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSubmit(); }}
                disabled={isSubmitting || (!applicationData.resume && !selectedCvFromLibrary && applicationData.selectedDocuments.length === 0) || !applicationData.personalInfo.first_name || !applicationData.personalInfo.last_name || !applicationData.personalInfo.email}
                sx={{
                  background: brandGradient,
                  '&:hover': { 
                    background: brandGradient,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)'
                  },
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                variant="contained"
                sx={{
                  background: brandGradient,
                  '&:hover': { 
                    background: brandGradient,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)'
                  },
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
      </Dialog>

      <AuthModal
        open={authModalOpen}
        initialTab={authModalTab}
        disableRedirect
        onClose={() => setAuthModalOpen(false)}
      />
    </>
  );
};

export default JobApplicationModal;
